import React, { useState, useMemo } from 'react';
import {
  Button,
  Input,
  Select,
  Space,
  Typography,
  Alert,
  Collapse,
  Tag,
  Divider,
  Spin,
  message,
  InputNumber,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ApiOutlined,
  SendOutlined,
  CheckOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { autoReplyAPI } from '../services/api';

const { Text, Paragraph } = Typography;

// 可用的 Telegram 上下文参数列表
const AVAILABLE_PARAMS: Array<{ param: string; label: string; example: string; group: string }> = [
  // 用户信息
  { param: 'telegram_user_id',    label: '用户ID',    example: '123456789',   group: '用户' },
  { param: 'telegram_username',   label: '@username', example: 'johndoe',     group: '用户' },
  { param: 'telegram_first_name', label: '名字',      example: '张三',         group: '用户' },
  { param: 'telegram_last_name',  label: '姓氏',      example: '李',           group: '用户' },
  // 机器人信息
  { param: 'bot_id',          label: '机器人ID',       example: '987654321',          group: '机器人' },
  { param: 'bot_username',    label: '机器人@username', example: 'mybot',              group: '机器人' },
  { param: 'bot_name',        label: '机器人名称',      example: 'My Bot',             group: '机器人' },
  { param: 'bot_invite_link', label: '机器人邀请链接',  example: 'https://t.me/mybot', group: '机器人' },
  // 群组信息
  { param: 'telegram_group_id',          label: '群组ID',       example: '-100123456',              group: '群组' },
  { param: 'telegram_group_title',       label: '群组标题',     example: '我的群组',                 group: '群组' },
  { param: 'telegram_group_invite_link', label: '群组邀请链接', example: 'https://t.me/joinchat/x', group: '群组' },
  // 深度链接参数
  { param: 'start_param', label: '深度链接参数', example: 'agent_007', group: '用户' },
];

// param 名 → example 快查表
const PARAM_EXAMPLE: Record<string, string> = Object.fromEntries(
  AVAILABLE_PARAMS.map(p => [p.param, p.example])
);

// param 名 → label 快查表
const PARAM_LABEL: Record<string, string> = Object.fromEntries(
  AVAILABLE_PARAMS.map(p => [p.param, p.label])
);

export interface RequestBodyParam {
  id: string;
  key: string;
  param: string;
}

export interface AuthCodeConfig {
  variable_name: string;
  expires_minutes: number;
}

/**
 * 动态变量配置格式
 * 机器人发送消息前会调用此 API，将返回字段注入为模板变量
 */
export interface DynamicVariableConfig {
  api_url: string;
  method: 'GET' | 'POST';
  request_body_params?: RequestBodyParam[];
  response_fields: string[];
  auth_codes?: AuthCodeConfig[];
}

interface Props {
  value?: DynamicVariableConfig | null;
  onChange?: (v: DynamicVariableConfig | null) => void;
}

let _nextParamId = 1;
const newParamId = () => `p${_nextParamId++}`;

const emptyConfig = (): DynamicVariableConfig => ({
  api_url: '',
  method: 'POST',
  request_body_params: [{ id: newParamId(), key: 'telegram_user_id', param: 'telegram_user_id' }],
  response_fields: [],
  auth_codes: [],
});

const DynamicVariableEditor: React.FC<Props> = ({ value, onChange }) => {
  // 确保旧数据（无 id 字段）向后兼容
  const config: DynamicVariableConfig = value
    ? {
        ...value,
        request_body_params: (value.request_body_params || []).map(p =>
          p.id ? p : { ...p, id: newParamId() }
        ),
      }
    : emptyConfig();

  // 测试发送的模拟值，key = param 名，value = 用户填写的模拟值
  const [testContext, setTestContext] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    status?: number;
    data?: any;
    error?: string;
  } | null>(null);

  const update = (next: DynamicVariableConfig | null) => {
    onChange?.(next);
  };

  const handleEnable = () => {
    update(emptyConfig());
  };

  const handleDisable = () => {
    update(null);
    setTestResult(null);
  };

  const updateField = <K extends keyof DynamicVariableConfig>(
    field: K,
    val: DynamicVariableConfig[K]
  ) => {
    update({ ...config, [field]: val });
  };

  // request_body_params 操作
  const addBodyParam = (paramName?: string) => {
    const existing = config.request_body_params || [];
    const param = paramName || 'telegram_user_id';
    if (existing.some(p => p.param === param)) return;
    update({ ...config, request_body_params: [...existing, { id: newParamId(), key: param, param }] });
  };

  const updateBodyParamKey = (idx: number, key: string) => {
    const next = (config.request_body_params || []).map((p, i) => i === idx ? { ...p, key } : p);
    update({ ...config, request_body_params: next });
  };

  const removeBodyParam = (idx: number) => {
    const next = (config.request_body_params || []).filter((_, i) => i !== idx);
    update({ ...config, request_body_params: next });
  };

  // response_fields 操作
  const addResponseField = () => {
    update({ ...config, response_fields: [...config.response_fields, ''] });
  };

  const updateResponseField = (idx: number, val: string) => {
    const next = config.response_fields.map((f, i) => (i === idx ? val : f));
    update({ ...config, response_fields: next });
  };

  const removeResponseField = (idx: number) => {
    const next = config.response_fields.filter((_, i) => i !== idx);
    update({ ...config, response_fields: next });
  };

  const addResponseFieldFromResult = (fieldName: string) => {
    if (config.response_fields.includes(fieldName)) return;
    update({ ...config, response_fields: [...config.response_fields, fieldName] });
  };

  // 获取某个 param 的测试值：优先用用户输入，其次用 AVAILABLE_PARAMS 的 example
  const getTestValue = (paramName: string): string =>
    testContext[paramName] ?? PARAM_EXAMPLE[paramName] ?? '';

  const setTestValue = (paramName: string, val: string) => {
    setTestContext(prev => ({ ...prev, [paramName]: val }));
  };

  // 根据当前 bodyParams 和 testContext 计算实际将发送的请求体（仅供预览）
  const previewRequestBody = useMemo(() => {
    const bodyParams = config.request_body_params || [];
    if (bodyParams.length === 0) {
      return { telegram_user_id: getTestValue('telegram_user_id') };
    }
    return Object.fromEntries(
      bodyParams
        .filter(p => p.key)
        .map(p => [p.key, getTestValue(p.param)])
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.request_body_params, testContext]);

  // 计算需要在测试区展示输入框的参数（去重 param）
  const testInputParams = useMemo(() => {
    const bodyParams = config.request_body_params || [];
    if (config.method === 'GET') {
      return [{ param: 'telegram_user_id', keys: ['telegram_user_id'] }];
    }
    const seen = new Set<string>();
    const result: Array<{ param: string; keys: string[] }> = [];
    for (const bp of bodyParams) {
      if (!seen.has(bp.param)) {
        seen.add(bp.param);
        result.push({ param: bp.param, keys: bodyParams.filter(p => p.param === bp.param).map(p => p.key) });
      }
    }
    return result;
  }, [config.request_body_params, config.method]);

  // 测试发送
  const handleTest = async () => {
    if (!config.api_url) {
      message.warning('请先填写接口地址');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await autoReplyAPI.testDynamicVar({
        api_url: config.api_url,
        method: config.method,
        request_body_params: config.request_body_params,
        test_context: {
          telegram_user_id:          Number(getTestValue('telegram_user_id')) || undefined,
          telegram_username:         getTestValue('telegram_username') || undefined,
          telegram_first_name:       getTestValue('telegram_first_name') || undefined,
          telegram_last_name:        getTestValue('telegram_last_name') || undefined,
          bot_id:                    Number(getTestValue('bot_id')) || undefined,
          bot_username:              getTestValue('bot_username') || undefined,
          bot_name:                  getTestValue('bot_name') || undefined,
          bot_invite_link:           getTestValue('bot_invite_link') || undefined,
          telegram_group_id:         Number(getTestValue('telegram_group_id')) || undefined,
          telegram_group_title:      getTestValue('telegram_group_title') || undefined,
          telegram_group_invite_link:getTestValue('telegram_group_invite_link') || undefined,
        },
      });
      setTestResult(res.data);
    } catch (err: any) {
      const errData = err.response?.data;
      setTestResult({ success: false, error: errData?.error || err.message });
    } finally {
      setTesting(false);
    }
  };

  // 递归收集响应 JSON 的所有叶子路径（最多两层）
  const collectFieldPaths = (obj: any, prefix = ''): string[] => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
    return Object.keys(obj).flatMap(k => {
      const path = prefix ? `${prefix}.${k}` : k;
      const val = obj[k];
      if (val && typeof val === 'object' && !Array.isArray(val) && prefix === '') {
        return [path, ...Object.keys(val).map(kk => `${path}.${kk}`)];
      }
      return [path];
    });
  };

  if (!value) {
    return (
      <div style={{ padding: '8px 0' }}>
        <Button icon={<ApiOutlined />} onClick={handleEnable} size="small">
          启用动态变量（调用外部 API）
        </Button>
        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
          可在发送前自动从你的服务器获取登录链接、Token 等动态内容
        </Text>
      </div>
    );
  }

  const configuredFields = config.response_fields.filter(f => f.trim());
  const bodyParams = config.request_body_params || [];
  const usedParams = new Set(bodyParams.map(p => p.param));
  const authCodes = config.auth_codes || [];

  const addAuthCode = () => {
    update({ ...config, auth_codes: [...authCodes, { variable_name: '', expires_minutes: 10 }] });
  };

  const updateAuthCode = (idx: number, patch: Partial<AuthCodeConfig>) => {
    const next = authCodes.map((c, i) => i === idx ? { ...c, ...patch } : c);
    update({ ...config, auth_codes: next });
  };

  const removeAuthCode = (idx: number) => {
    update({ ...config, auth_codes: authCodes.filter((_, i) => i !== idx) });
  };

  return (
    <Collapse
      defaultActiveKey={['config']}
      style={{ marginBottom: 0 }}
      items={[
        {
          key: 'config',
          label: (
            <Space>
              <ApiOutlined />
              <Text strong>动态变量 API 配置</Text>
              {configuredFields.length > 0 && (
                <Space size={4}>
                  {configuredFields.map(f => (
                    <Tag key={f} color="blue">{`{${f}}`}</Tag>
                  ))}
                </Space>
              )}
            </Space>
          ),
          children: (
            <div>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="工作原理"
                description={
                  <Paragraph style={{ margin: 0, fontSize: 12 }}>
                    机器人发送消息时，会自动调用下方配置的接口，并将返回字段注入为模板变量。
                    <br />
                    例如接口返回 <Text code>{`{"login_token":"abc","uid":"123"}`}</Text>，
                    则可在回复文本和按钮 URL 中使用 <Text code>{'{login_token}'}</Text>、<Text code>{'{uid}'}</Text>。
                    <br />
                    接口鉴权密钥通过服务器 <Text code>.env</Text> 中的 <Text code>DYNAMIC_VAR_API_KEY</Text> 配置，
                    以 <Text code>Authorization: Bearer ...</Text> 方式发送。
                  </Paragraph>
                }
              />

              {/* 接口地址 */}
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>接口地址</Text>
                <Input
                  placeholder="https://yourapp.com/api/bot/register"
                  value={config.api_url}
                  onChange={e => updateField('api_url', e.target.value)}
                  style={{ marginTop: 4 }}
                />
              </div>

              {/* 请求方式 */}
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>请求方式</Text>
                <Select
                  value={config.method}
                  onChange={val => updateField('method', val)}
                  style={{ width: '100%', marginTop: 4 }}
                  options={[
                    { value: 'POST', label: 'POST' },
                    { value: 'GET', label: 'GET（固定携带 ?telegram_user_id=xxx）' },
                  ]}
                />
              </div>

              {/* 请求体参数（仅 POST） */}
              {config.method === 'POST' && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ marginBottom: 6 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>请求体参数</Text>
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 8, color: '#999' }}>
                      点击下方参数快速添加，或手动配置字段名
                    </Text>
                  </div>

                  {/* 可用参数快捷按钮 */}
                  <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {AVAILABLE_PARAMS.map(({ param, label }) => (
                      <Tag
                        key={param}
                        color={usedParams.has(param) ? 'blue' : 'default'}
                        style={{ cursor: usedParams.has(param) ? 'default' : 'pointer', userSelect: 'none' }}
                        onClick={() => !usedParams.has(param) && addBodyParam(param)}
                        title={`示例值：${PARAM_EXAMPLE[param]}`}
                      >
                        {usedParams.has(param) && <CheckOutlined style={{ marginRight: 2 }} />}
                        {param}
                        <Text type="secondary" style={{ fontSize: 10, marginLeft: 3 }}>({label})</Text>
                      </Tag>
                    ))}
                  </div>

                  {/* 已添加的参数列表 */}
                  {bodyParams.length === 0 && (
                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                      请点击上方参数添加，默认会携带 telegram_user_id
                    </Text>
                  )}
                  {bodyParams.map((item, idx) => (
                    <Space key={item.id} style={{ marginBottom: 4, display: 'flex' }}>
                      <Input
                        size="small"
                        placeholder="发送字段名"
                        style={{ width: 160 }}
                        value={item.key}
                        onChange={e => updateBodyParamKey(idx, e.target.value)}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>←</Text>
                      <Select
                        size="small"
                        style={{ width: 200 }}
                        value={item.param}
                        onChange={val => {
                          const next = bodyParams.map((p, i) =>
                            i === idx ? { id: p.id, key: val, param: val } : p
                          );
                          update({ ...config, request_body_params: next });
                        }}
                        options={AVAILABLE_PARAMS.map(p => ({
                          value: p.param,
                          label: `${p.param} (${p.label})`,
                        }))}
                      />
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeBodyParam(idx)}
                      />
                    </Space>
                  ))}
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      const unused = AVAILABLE_PARAMS.find(p => !usedParams.has(p.param));
                      addBodyParam(unused?.param);
                    }}
                    style={{ marginTop: 4 }}
                  >
                    添加参数
                  </Button>
                </div>
              )}

              <Divider style={{ margin: '8px 0' }} />

              {/* 提取响应字段 */}
              <div style={{ marginBottom: 12 }}>
                <Space style={{ marginBottom: 6 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>从响应中提取的字段（即模板变量名）</Text>
                  <Button size="small" icon={<PlusOutlined />} onClick={addResponseField}>
                    添加字段
                  </Button>
                </Space>
                {config.response_fields.length === 0 && (
                  <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                    请添加要提取的字段，如 login_token、uid，也可通过下方测试发送后点击字段自动添加
                  </Text>
                )}
                {config.response_fields.map((field, idx) => (
                  <Space key={`rf-${idx}-${field}`} style={{ marginBottom: 4, display: 'flex' }}>
                    <Input
                      size="small"
                      placeholder="字段名，如 login_token"
                      style={{ width: 200 }}
                      value={field}
                      onChange={e => updateResponseField(idx, e.target.value)}
                      addonBefore="{"
                      addonAfter="}"
                    />
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeResponseField(idx)}
                    />
                  </Space>
                ))}
              </div>

              <Divider style={{ margin: '8px 0' }} />

              {/* 测试发送 */}
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                  测试发送
                </Text>

                {/* 每个 param 一个输入框，显示将使用的模拟值 */}
                <div style={{ marginBottom: 8 }}>
                  {testInputParams.map(({ param, keys }) => (
                    <Space key={param} style={{ marginBottom: 4, display: 'flex', alignItems: 'center' }}>
                      <Input
                        size="small"
                        style={{ width: 200 }}
                        value={testContext[param] ?? PARAM_EXAMPLE[param] ?? ''}
                        onChange={e => setTestValue(param, e.target.value)}
                        placeholder={PARAM_EXAMPLE[param] || param}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {PARAM_LABEL[param] || param}
                        {config.method === 'POST' && keys.length > 0 && (
                          <span style={{ color: '#bbb', marginLeft: 4 }}>
                            → 请求体字段：{keys.join(', ')}
                          </span>
                        )}
                      </Text>
                    </Space>
                  ))}
                </div>

                {/* 请求体预览（POST） */}
                {config.method === 'POST' && bodyParams.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>将发送的请求体：</Text>
                    <pre
                      style={{
                        margin: '4px 0 0',
                        fontSize: 11,
                        background: '#f6f8fa',
                        border: '1px solid #e1e4e8',
                        borderRadius: 4,
                        padding: '4px 8px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {JSON.stringify(previewRequestBody, null, 2)}
                    </pre>
                  </div>
                )}

                <Button
                  size="small"
                  icon={<SendOutlined />}
                  loading={testing}
                  onClick={handleTest}
                  type="primary"
                  ghost
                >
                  发送测试
                </Button>

                {testing && <Spin size="small" style={{ marginTop: 8, display: 'block' }} />}

                {testResult && (
                  <div style={{ marginTop: 8 }}>
                    {testResult.success ? (
                      <>
                        <Text type="success" style={{ fontSize: 12 }}>
                          响应成功（HTTP {testResult.status}）— 点击字段名可快速添加到提取列表
                        </Text>
                        <div
                          style={{
                            background: '#f6f8fa',
                            border: '1px solid #e1e4e8',
                            borderRadius: 4,
                            padding: '8px 10px',
                            marginTop: 6,
                            maxHeight: 200,
                            overflowY: 'auto',
                          }}
                        >
                          <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {JSON.stringify(testResult.data, null, 2)}
                          </pre>
                        </div>
                        {testResult.data && typeof testResult.data === 'object' && (
                          <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>可提取的字段：</Text>
                            <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {collectFieldPaths(testResult.data).map(path => {
                                const varName = path.split('.').pop() as string;
                                const isAdded =
                                  config.response_fields.includes(path) ||
                                  config.response_fields.includes(varName);
                                return (
                                  <Tag
                                    key={path}
                                    color={isAdded ? 'green' : 'processing'}
                                    style={{ cursor: isAdded ? 'default' : 'pointer' }}
                                    onClick={() => !isAdded && addResponseFieldFromResult(path)}
                                    title={isAdded ? '已添加' : '点击添加到提取字段'}
                                  >
                                    {isAdded && <CheckOutlined style={{ marginRight: 2 }} />}
                                    {path}
                                  </Tag>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <Alert
                        type="error"
                        showIcon
                        style={{ marginTop: 6 }}
                        message={testResult.error || '请求失败'}
                      />
                    )}
                  </div>
                )}
              </div>

              <Divider style={{ margin: '8px 0' }} />

              <Button size="small" danger onClick={handleDisable}>
                禁用动态变量
              </Button>
            </div>
          ),
        },
        {
          key: 'auth_codes',
          label: (
            <Space>
              <KeyOutlined />
              <Text strong>登录短码变量</Text>
              {authCodes.filter(c => c.variable_name).length > 0 && (
                <Space size={4}>
                  {authCodes.filter(c => c.variable_name).map(c => (
                    <Tag key={c.variable_name} color="orange">{`{${c.variable_name}}`}</Tag>
                  ))}
                </Space>
              )}
            </Space>
          ),
          children: (
            <div>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="工作原理"
                description={
                  <Paragraph style={{ margin: 0, fontSize: 12 }}>
                    机器人发送消息时，自动生成一次性短码（16字符），嵌入消息文本或按钮 URL 中。
                    <br />
                    你的 Web 服务收到短码后，调用 <Text code>POST /api/tg-auth/exchange</Text>（携带 <Text code>X-API-Key</Text>）换取用户信息，实现免密登录。
                    <br />
                    短码一次性有效，使用后软删除（保留记录供审计）。API Key 在服务器 <Text code>.env</Text> 中配置 <Text code>TG_AUTH_API_KEY</Text>。
                  </Paragraph>
                }
              />

              {authCodes.length === 0 && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
                  暂未配置短码变量，点击下方按钮添加
                </Text>
              )}

              {authCodes.map((item, idx) => (
                <Space key={idx} style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}>
                  <Input
                    size="small"
                    placeholder="变量名，如 auth_code"
                    style={{ width: 160 }}
                    value={item.variable_name}
                    onChange={e => updateAuthCode(idx, { variable_name: e.target.value })}
                    addonBefore="{"
                    addonAfter="}"
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>有效期</Text>
                  <InputNumber
                    size="small"
                    min={1}
                    max={1440}
                    value={item.expires_minutes}
                    onChange={v => updateAuthCode(idx, { expires_minutes: v || 10 })}
                    style={{ width: 80 }}
                    addonAfter="分钟"
                  />
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeAuthCode(idx)}
                  />
                </Space>
              ))}

              <Button size="small" icon={<PlusOutlined />} onClick={addAuthCode} style={{ marginTop: 4 }}>
                添加短码变量
              </Button>

              {authCodes.filter(c => c.variable_name).length > 0 && (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={
                    <span style={{ fontSize: 12 }}>
                      在回复文本或按钮 URL 中使用：
                      {authCodes.filter(c => c.variable_name).map(c => (
                        <Text key={c.variable_name} code style={{ marginLeft: 4 }}>{`{${c.variable_name}}`}</Text>
                      ))}
                      <br />
                      示例：<Text code>{`https://yourweb.com/login?code={${authCodes.find(c => c.variable_name)?.variable_name || 'auth_code'}}`}</Text>
                    </span>
                  }
                />
              )}
            </div>
          ),
        },
      ]}
    />
  );
};

export default DynamicVariableEditor;
